import {RefObject, useMemo} from 'react';
import {WebcamRefTypes} from '@chekinapp/ui';
import {useClientFaceDetection} from './useClientFaceDetection';
import {useServerFaceDetection} from './useServerFaceDetection';
import {useFaceModelsStore} from '../store';
import {canUseClientSideDetection} from '../utils/deviceCapabilities';
import type {FacePosition, FaceDetectionMechanism, OnFaceDetectionSignal} from '../types';

interface UseAdaptiveFaceDetectionProps {
  videoRef?: RefObject<WebcamRefTypes>;
  forceMechanism?: FaceDetectionMechanism;
  enabled?: boolean;
  onSignal?: OnFaceDetectionSignal;
}

export const useAdaptiveFaceDetection = ({
  videoRef,
  forceMechanism = 'AUTO',
  enabled = true,
  onSignal,
}: UseAdaptiveFaceDetectionProps = {}) => {
  const setIsWebGLSupported = useFaceModelsStore(state => state.setIsWebGLSupported);

  const useClientDetection = useMemo(() => {
    if (!enabled) return false;
    return canUseClientSideDetection({
      forceMechanism,
      setIsWebGLSupported,
    });
  }, [enabled, forceMechanism, setIsWebGLSupported]);

  const clientDetection = useClientFaceDetection({
    videoRef,
    shouldPreload: enabled && useClientDetection === true,
    onSignal,
  });

  const serverDetection = useServerFaceDetection({
    videoRef,
    shouldInitialize: enabled && useClientDetection === false,
    onSignal,
  });

  return {
    isUsingClientDetection: useClientDetection,
    ...(useClientDetection ? clientDetection : serverDetection),
  };
};

export type {FacePosition};
